import { getFolders } from '../collectionFolders';
import { blankSecretValues, redactSettingsSecrets } from './redactSecrets';

/**
 * Apiboo Collection Format v1.0 — the NATIVE export.
 *
 * Every other export translates into somebody else's schema, and each of those
 * translations sheds something: Postman has no boo-dialect scripts, OpenAPI has
 * no mock examples or cookies, Bruno flattens settings. The warnings plumbing
 * exists precisely to confess those losses. This format's reason to exist is
 * that it has NOTHING to confess — it is the interchange shape both pipelines
 * already speak (collectionImport/types.js), wrapped in a versioned envelope,
 * so export → import is lossless BY CONSTRUCTION rather than by effort.
 *
 * ## The envelope
 *
 *     {
 *       "apiboo": { "format": "collection", "version": "1.0" },
 *       "collections": [ …NormalizedCollection ]
 *     }
 *
 * - `apiboo.format` is the DETECTION KEY (detectImportFormat matches on it),
 *   namespaced so no foreign export can collide with it by accident, and named
 *   so a future vault/workspace envelope can share the marker with a different
 *   `format` value.
 * - `version` is "MAJOR.MINOR" as a STRING. Minor bumps are additive: a 1.0
 *   reader given a 1.3 file imports what it understands and drops the rest —
 *   acceptable, because everything in v1.x is data a request can live without
 *   losing identity. A MAJOR bump means the shape itself changed, and the
 *   reader must REFUSE rather than half-import credentials-adjacent config it
 *   misreads (parseApiboo enforces this).
 * - `collections` is an ARRAY even though v1.0 always writes one — so
 *   multi-collection export is a minor bump someday, not a new envelope.
 *
 * ## What is deliberately NOT exported
 *
 * Server identity (collection/request row ids, owner_id, workspace_id,
 * can_edit, timestamps): a file that carries another workspace's row ids
 * invites some future reader to trust them. Folder ids ARE kept — they are the
 * only thing wiring `requests[].folder_id` to `folders[].id` — but they are
 * meaningful ONLY within the file, and the importer assigns fresh ones.
 */

export const APIBOO_COLLECTION_FORMAT = 'collection';
export const APIBOO_COLLECTION_VERSION = '1.0';

const str = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v));

/** [{key, value, enabled}] — the shape headers/params/cookies all share. */
function kvRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r) => r && typeof r === 'object')
    .map((r) => ({ key: str(r.key), value: str(r.value), enabled: r.enabled !== false }));
}

/** Deep-copy a plain JSON value; anything unserializable becomes undefined. */
function jsonClone(v) {
  if (v == null || typeof v !== 'object') return v ?? undefined;
  try {
    return JSON.parse(JSON.stringify(v));
  } catch {
    return undefined;
  }
}

/** Drop undefined values so the file stays readable and diffs stay honest. */
function compact(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

function exportRequest(req, index) {
  return compact({
    name: str(req.name) || 'Request',
    description: str(req.description) || undefined,
    method: (str(req.method) || 'GET').toUpperCase(),
    url: str(req.url),
    headers: kvRows(req.headers),
    params: kvRows(req.params),
    cookies: kvRows(req.cookies),
    body: str(req.body),
    // Kept VERBATIM ('json'|'form'|'formdata'|'raw'|'binary'…): this is our own
    // format, so normalising to some other tool's vocabulary would be the exact
    // lossiness the format exists to avoid.
    body_type: str(req.body_type || req.bodyType) || 'json',
    auth: jsonClone(req.auth) ?? undefined,
    settings: jsonClone(req.settings) ?? undefined,
    formDataFields: Array.isArray(req.formDataFields) ? jsonClone(req.formDataFields) : undefined,
    folder_id: req.folder_id != null ? str(req.folder_id) : undefined,
    sort_order: Number.isFinite(req.sort_order) ? req.sort_order : index,
    preRequestScript: str(req.preRequestScript || req.pre_request_script) || undefined,
    testScript: str(req.testScript || req.test_script) || undefined,
    // 'boo' unless the request still carries imported-Postman scripts. Losing
    // this flag would make a round-tripped 'pm' script execute as boo.
    scriptDialect: str(req.scriptDialect) || undefined,
    protocolProfileBehavior: jsonClone(req.protocolProfileBehavior) ?? undefined,
    mock: jsonClone(req.mock) ?? undefined,
  });
}

function exportFolder(folder, index) {
  return compact({
    id: str(folder.id),
    name: str(folder.name) || 'Folder',
    parent_id: folder.parent_id != null ? str(folder.parent_id) : null,
    sort_order: Number.isFinite(folder.sort_order) ? folder.sort_order : index,
    auth: jsonClone(folder.auth) ?? undefined,
    preRequestScript: str(folder.preRequestScript) || undefined,
    testScript: str(folder.testScript) || undefined,
    // A secret row keeps its name and `secret: true`, never its value (F2).
    variables: Array.isArray(folder.variables) ? jsonClone(blankSecretValues(folder.variables)) : undefined,
  });
}

/**
 * Build the v1.0 envelope for one collection.
 *
 * Takes the same hydrated collection object every other `to*` exporter takes.
 * The `warnings` collector is accepted for interface parity and never pushed
 * to — an Apiboo export with a warning would mean the format has failed at the
 * one thing it is for.
 */
export function toApibooCollection(collection, _warnings) {
  const folders = getFolders(collection).map(exportFolder);
  const requests = (Array.isArray(collection.requests) ? collection.requests : []).map(
    exportRequest
  );

  return {
    apiboo: { format: APIBOO_COLLECTION_FORMAT, version: APIBOO_COLLECTION_VERSION },
    collections: [
      compact({
        name: str(collection.name) || 'Collection',
        description: str(collection.description) || undefined,
        settings: jsonClone(redactSettingsSecrets(collection.settings)) ?? undefined,
        preRequestScript: str(collection.preRequestScript) || undefined,
        testScript: str(collection.testScript) || undefined,
        folders,
        requests,
      }),
    ],
  };
}
